import React, { useRef, useState } from 'react';
import { Box, IconButton, TextField, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Add, DeleteOutline } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { Signatory } from './certificate-model';
import { useCertificateAssetUpload } from './useCertificateAssetUpload';

const useStyles = makeStyles(() =>
  createStyles({
    signatory: { border: '1px solid #EFEFEF', borderRadius: '6px', padding: '12px', marginBottom: '12px' },
    row: { display: 'flex', gap: '8px', alignItems: 'flex-start' },
    image: {
      marginTop: '10px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    preview: {
      height: '40px',
      maxWidth: '160px',
      objectFit: 'contain',
      background: '#FAFAFA',
      border: '1px solid #EFEFEF',
      borderRadius: '4px',
      padding: '2px 6px',
    },
    helper: { fontSize: '12px', color: '#808080' },
  })
);

interface Props {
  signatories: Signatory[];
  onChange: (signatories: Signatory[]) => void;
}

export const SignaturesEditor = ({ signatories, onChange }: Props) => {
  const classes = useStyles();
  const { setSnackbar } = useSnackbar();
  const { upload } = useCertificateAssetUpload();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadIndex, setUploadIndex] = useState<number>();

  const set = (index: number, patch: Partial<Signatory>) =>
    onChange(signatories.map((signatory, i) => (i === index ? { ...signatory, ...patch } : signatory)));

  const pickImage = (index: number) => {
    setUploadIndex(index);
    if (fileRef.current) fileRef.current.click();
  };

  const onFile = async (file?: File) => {
    if (file === undefined || uploadIndex === undefined) return;
    try {
      set(uploadIndex, { image: await upload('signature', file) });
    } catch (e) {
      setSnackbar({ open: true, variant: 'error', message: (e as Error).message });
    } finally {
      setUploadIndex(undefined);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Box>
      {signatories.map((signatory, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={index} className={classes.signatory}>
          <Box className={classes.row}>
            <TextField
              variant='outlined'
              size='small'
              label='Name'
              value={signatory.name}
              onChange={(e) => set(index, { name: e.target.value })}
            />
            <TextField
              variant='outlined'
              size='small'
              label='Position'
              value={signatory.title}
              onChange={(e) => set(index, { title: e.target.value })}
            />
            <IconButton
              size='small'
              aria-label='Remove signature'
              style={{ marginTop: 6 }}
              onClick={() => onChange(signatories.filter((_unused, i) => i !== index))}
            >
              <DeleteOutline fontSize='small' />
            </IconButton>
          </Box>
          <Box className={classes.image}>
            {signatory.image && <img className={classes.preview} src={signatory.image.url} alt='Signature' />}
            <TabletButton
              variant='text'
              color='primary'
              size='small'
              disabled={uploadIndex === index}
              onClick={() => pickImage(index)}
            >
              {signatory.image ? 'Replace signature' : 'Upload signature'}
            </TabletButton>
            {signatory.image && (
              <TabletButton variant='text' color='error' size='small' onClick={() => set(index, { image: null })}>
                Remove
              </TabletButton>
            )}
          </Box>
        </Box>
      ))}
      {signatories.length < 2 && (
        <TabletButton
          variant='text'
          color='primary'
          startIcon={<Add />}
          onClick={() => onChange([...signatories, { name: '', title: '', image: null }])}
        >
          Add signature
        </TabletButton>
      )}
      <Typography className={classes.helper}>A PNG with a transparent background looks best.</Typography>
      <input
        ref={fileRef}
        type='file'
        accept='image/png,image/jpeg'
        hidden
        onChange={(e) => onFile(e.target.files?.[0])}
      />
    </Box>
  );
};
